import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useNotify } from 'react-admin';
import { AGENT_STATUS_COLORS, AGENT_STATUS_DISPLAY } from '../../../constants/agentStatus';
import { AgentAvailabilityStatus } from '../../../interfaces/calls';
import callsService from '../../../services/appointments/calls';

interface OverrideAgentStatusModalProps {
  open: boolean;
  onClose: () => void;
  agent: {
    id: number;
    name: string;
    currentStatus: AgentAvailabilityStatus;
  } | null;
  onSuccess: () => void;
}

export const OverrideAgentStatusModal = ({
  open,
  onClose,
  agent,
  onSuccess,
}: OverrideAgentStatusModalProps) => {
  const [selectedStatus, setSelectedStatus] = useState<AgentAvailabilityStatus | ''>('');
  const [loading, setLoading] = useState(false);
  const notify = useNotify();

  const handleSubmit = async () => {
    if (!agent || !selectedStatus) {
      return;
    }

    try {
      setLoading(true);
      const response = await callsService.overrideAgentStatus(agent.id, selectedStatus);

      if (response.success) {
        notify('Agent status updated successfully', { type: 'success' });
        onSuccess();
        onClose();
        setSelectedStatus('');
      } else {
        notify(response.message || 'Failed to update agent status', { type: 'error' });
      }
    } catch (error) {
      notify('Failed to update agent status', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedStatus('');
    onClose();
  };

  const statusOptions = Object.values(AgentAvailabilityStatus).filter(
    (status) => status !== AgentAvailabilityStatus.DISABLED,
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>Override Agent Status</DialogTitle>
      <DialogContent>
        {agent && (
          <Box display='flex' flexDirection='column' gap={2} mt={1}>
            <Box>
              <Typography variant='body2' color='textSecondary'>
                Agent
              </Typography>
              <Typography variant='body1' fontWeight={500}>
                {agent.name}
              </Typography>
            </Box>

            <Box>
              <Typography variant='body2' color='textSecondary'>
                Current Status
              </Typography>
              <Box
                display='inline-block'
                px={1.5}
                py={0.5}
                borderRadius={1}
                mt={0.5}
                sx={{
                  backgroundColor: AGENT_STATUS_COLORS[agent.currentStatus],
                  color: 'white',
                  fontSize: '14px',
                }}>
                {AGENT_STATUS_DISPLAY[agent.currentStatus]}
              </Box>
            </Box>

            <FormControl fullWidth>
              <InputLabel id='status-select-label'>New Status</InputLabel>
              <Select
                labelId='status-select-label'
                value={selectedStatus}
                label='New Status'
                onChange={(e) => setSelectedStatus(e.target.value as AgentAvailabilityStatus)}>
                {statusOptions.map((status) => (
                  <MenuItem key={status} value={status}>
                    <Box display='flex' alignItems='center' gap={1}>
                      <Box
                        width={12}
                        height={12}
                        borderRadius='50%'
                        sx={{ backgroundColor: AGENT_STATUS_COLORS[status] }}
                      />
                      {AGENT_STATUS_DISPLAY[status]}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant='caption' color='textSecondary'>
              Note: This will override the agent's current status. The agent can change it back at
              any time.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant='contained'
          disabled={!selectedStatus || loading || selectedStatus === agent?.currentStatus}>
          Update Status
        </Button>
      </DialogActions>
    </Dialog>
  );
};
